/*
 * CommissionLedgerV2Panel — reusable ledger table.
 *
 * Renders totals-by-status, filter bar, rows table, and an "Inspect Record"
 * modal on row-click. Consumed by:
 *   - /plog/commission_ledger_v2  (full-page)
 *   - /admin/analytics?tab=affiliates  (embedded)
 *
 * Guardian Case link:
 *   When the source_customer has an open case in `guardian_cases`, the
 *   inspect modal shows "View Guardian Case" that deep-links to
 *   /admin/analytics?tab=guardian. Otherwise the button is disabled.
 */
import { useEffect, useState } from "react";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";
import { RefreshCw, Filter, Zap, X, ExternalLink, Shield, Download } from "lucide-react";

const STATUS_COLORS = {
  PENDING:   "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  AVAILABLE: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  SCHEDULED: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  PAID:      "bg-indigo-500/15 text-indigo-300 ring-indigo-500/30",
  FROZEN:    "bg-slate-500/15 text-slate-300 ring-slate-500/30",
  REVERSED:  "bg-red-500/15 text-red-300 ring-red-500/30",
};

const cents = (c) => (c == null ? "—" : `$${(Number(c) / 100).toFixed(2)}`);

function InspectModal({ row, onClose }) {
  const [guardianCase, setGuardianCase] = useState(null);
  const [guardianLoading, setGuardianLoading] = useState(true);

  useEffect(() => {
    // Guardian dedupes cases per affiliate (fraud_cases.user_id), so we
    // look up by the ledger row's recipient (the affiliate receiving the
    // commission), not by the buyer — buyer_user_id is NULL on chargeback
    // and high_value cases and would silently miss them.
    if (!row?.recipient_user_id) { setGuardianLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await authAxios.get(
          `/admin/fraud/cases?user_id=${encodeURIComponent(row.recipient_user_id)}&limit=10`
        );
        const rows = r.data?.rows || [];
        const active = rows.find((c) => c.status && c.status !== "cleared") || null;
        if (!cancelled) setGuardianCase(active);
      } catch {
        if (!cancelled) setGuardianCase(null);
      } finally {
        if (!cancelled) setGuardianLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row?.recipient_user_id]);

  if (!row) return null;

  const openGuardian = () => {
    // Navigate directly to the consolidated FraudDefender page for the
    // source customer (choice 2b — one canonical case surface). Falls
    // back to the guardian tab if we don't have a user_id resolved.
    const cid = guardianCase?.case_id;
    const uid = guardianCase?.user_id || row?.source_customer_id;
    if (uid) {
      const suffix = cid ? `?case_id=${encodeURIComponent(cid)}` : "";
      window.location.href = `/admin/fraud/defender/${uid}${suffix}`;
      return;
    }
    const url = cid
      ? `/admin/analytics?tab=guardian&case=${encodeURIComponent(cid)}`
      : `/admin/analytics?tab=guardian`;
    window.location.href = url;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose} data-testid="ledger-inspect-modal">
      <div className="bg-[#111113] border border-[#C5A55A]/30 rounded-xl w-full max-w-3xl my-8 max-h-[calc(100vh-4rem)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-[#111113] border-b border-white/10 flex items-center justify-between px-5 py-3 z-10">
          <div>
            <h2 className="text-sm font-semibold text-[#C5A55A]">Inspect Commission Record</h2>
            <p className="text-[10px] text-white/40 font-mono mt-0.5">{row.id}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="inspect-close-btn">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {/* Guardian Case link */}
          <section className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3" data-testid="inspect-guardian-section">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#C5A55A]" />
                <div>
                  <div className="text-xs font-semibold text-white/80">Guardian Case</div>
                  <div className="text-[10px] text-white/40">
                    {guardianLoading
                      ? "Checking…"
                      : guardianCase
                        ? `${(guardianCase.case_id || "").slice(0, 12)} · ${guardianCase.status?.toUpperCase() || "OPEN"} · ${guardianCase.tier || guardianCase.threat_class || "—"} · risk ${guardianCase.risk_score ?? "?"}`
                        : "No open case for source customer"}
                  </div>
                </div>
              </div>
              <button
                onClick={openGuardian}
                disabled={!guardianCase}
                className="text-xs px-3 py-1.5 rounded-lg bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 border border-[#C5A55A]/30 text-[#C5A55A] disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
                data-testid="inspect-guardian-btn"
              >
                <ExternalLink className="w-3 h-3" /> View Case
              </button>
            </div>
          </section>

          {/* Recipient + Source */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3">
              <div className="text-[10px] uppercase text-white/40 mb-2">Recipient</div>
              <div className="text-sm text-white/90">{row.recipient_name || "—"}</div>
              <div className="text-[10px] font-mono text-white/40 mt-0.5">{row.recipient_user_id}</div>
            </div>
            <div className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3">
              <div className="text-[10px] uppercase text-white/40 mb-2">Source Customer</div>
              <div className="text-sm text-white/90">{row.source_customer_name || "—"}</div>
              <div className="text-[10px] font-mono text-white/40 mt-0.5">{row.source_customer_id}</div>
            </div>
          </section>

          {/* Amounts */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <KV label="Rate" value={`${((row.commission_rate || 0) * 100).toFixed(0)}%`} tone="white" />
            <KV label="Gross" value={cents(row.gross_transaction_amount)} tone="white" />
            <KV label="Commission" value={cents(row.commission_amount)} tone={row.commission_amount < 0 ? "red" : "emerald"} />
            <KV label="Status" value={row.status} tone="gold" />
          </section>

          {/* Timeline */}
          <section className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3 space-y-1">
            <div className="text-[10px] uppercase text-white/40 mb-2">Timeline</div>
            <TimeRow label="Earned" value={row.earned_at} />
            <TimeRow label="Clears" value={row.clears_at} />
            <TimeRow label="Available" value={row.available_at} />
            <TimeRow label="Paid" value={row.paid_at} />
          </section>

          {/* Payment source */}
          <section className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3">
            <div className="text-[10px] uppercase text-white/40 mb-2">Payment Source</div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="text-white/50">Type</div>
              <div className="text-white/85 font-mono">{row.payment_source || "card"}</div>
              {row.subscription_id && (<><div className="text-white/50">Subscription</div><div className="text-white/85 font-mono truncate">{row.subscription_id}</div></>)}
              {row.coin_pack_id && (<><div className="text-white/50">Coin pack</div><div className="text-white/85 font-mono">{row.coin_pack_id}</div></>)}
              {row.stripe_session_id && (<><div className="text-white/50">Stripe session</div><div className="text-white/85 font-mono truncate">{row.stripe_session_id}</div></>)}
              {row.compensation_profile && (<><div className="text-white/50">Profile</div><div className="text-white/85 font-mono">{row.compensation_profile}</div></>)}
            </div>
          </section>

          {/* Raw */}
          <details className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3">
            <summary className="text-[10px] uppercase text-white/40 cursor-pointer">Raw record</summary>
            <pre className="mt-2 text-[10px] text-white/60 font-mono whitespace-pre-wrap break-all max-h-64 overflow-auto" data-testid="inspect-raw">
              {JSON.stringify(row, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value, tone = "white" }) {
  const toneMap = { white: "text-white/85", emerald: "text-emerald-300", red: "text-red-300", gold: "text-[#C5A55A]" };
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0a0a0a] p-3">
      <div className="text-[10px] uppercase text-white/40">{label}</div>
      <div className={`text-sm font-mono mt-1 ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}

function TimeRow({ label, value }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-white/50">{label}</span>
      <span className="text-white/80 font-mono">{value ? new Date(value).toLocaleString() : "—"}</span>
    </div>
  );
}

export default function CommissionLedgerV2Panel({ showAdminActions = true, initialUserId = "", range = null }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [filters, setFilters] = useState({ status: "all", commission_type: "all", user_id: initialUserId });
  const [selectedRow, setSelectedRow] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // When embedded (e.g. inside AffiliatesTab), `range` is driven by the
  // header timescale dropdown ("7d" / "30d" / "90d" / "1y" / "total") and
  // added to the backend query as `days`. When rendered as a standalone
  // page it defaults to null → all-time.
  const rangeToDays = (r) => ({ "7d": 7, "30d": 30, "90d": 90, "1y": 365, "total": 3650 }[r]);
  const days = range ? rangeToDays(range) : null;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.commission_type !== "all" && { commission_type: filters.commission_type }),
        ...(filters.user_id && { user_id: filters.user_id.trim() }),
        ...(days && { days: String(days) }),
      });
      const res = await authAxios.get(`/admin/commission-ledger/v2?${params}`);
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
      setTotals(res.data?.totals_by_status || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [filters, days, page]);

  // Reset to page 1 whenever filters or range change
  useEffect(() => { setPage(1); }, [filters, days]);

  const clearPending = async () => {
    setClearing(true);
    try {
      const res = await authAxios.post("/admin/commission-ledger/clear-pending");
      toast.success(`Promoted ${res.data?.promoted ?? 0} rows PENDING → AVAILABLE`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear pending");
    } finally {
      setClearing(false);
    }
  };

  // CSV export — honors the current filters (status / commission_type /
  // user_id / days) but pulls ALL matching rows in one shot (not just the
  // first 200 shown in the table). Downloads client-side so nothing hits
  // the network beyond the standard admin ledger endpoint.
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({
        limit: "10000",
        offset: "0",
        ...(filters.status !== "all" && { status: filters.status }),
        ...(filters.commission_type !== "all" && { commission_type: filters.commission_type }),
        ...(filters.user_id && { user_id: filters.user_id.trim() }),
        ...(days && { days: String(days) }),
      });
      const res = await authAxios.get(`/admin/commission-ledger/v2?${params}`);
      const allRows = res.data?.rows || [];
      if (allRows.length === 0) {
        toast.info("No rows to export for the current filters");
        return;
      }
      const columns = [
        "id", "earned_at", "commission_type", "compensation_profile",
        "recipient_user_id", "recipient_name",
        "source_customer_id", "source_customer_name",
        "commission_rate", "gross_transaction_amount", "commission_amount",
        "status", "clears_at", "available_at", "paid_at",
        "payment_source", "subscription_id", "coin_pack_id", "stripe_session_id",
      ];
      const escape = (v) => {
        if (v == null) return "";
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [columns.join(",")];
      for (const r of allRows) lines.push(columns.map((c) => escape(r[c])).join(","));
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `commission-ledger-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allRows.length} row${allRows.length === 1 ? "" : "s"}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
          : e?.message || "Failed to export CSV";
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  return (
    <section data-testid="commission-ledger-v2-panel" className="space-y-4">
      {/* Action row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] text-white/40">
          SIMPLE_2026_V1 · commission_ledger · CUSTOMER 20% / SPONSOR 6% / LEADERSHIP 4% / PRO 30%
        </div>
        <div className="flex items-center gap-2">
          {showAdminActions && (
            <button
              onClick={clearPending}
              disabled={clearing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 disabled:opacity-50"
              data-testid="clear-pending-btn"
              title="Manually promote PENDING → AVAILABLE (normally runs hourly via Arq)"
            >
              <Zap className={`w-3.5 h-3.5 ${clearing ? "animate-pulse" : ""}`} />
              {clearing ? "Promoting…" : "Clear PENDING → AVAILABLE"}
            </button>
          )}
          <button
            onClick={exportCsv}
            disabled={exporting || loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-50"
            data-testid="export-csv-btn"
            title="Download the full filtered ledger as CSV"
          >
            <Download className={`w-3.5 h-3.5 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Exporting…" : "Download CSV"}
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 border border-[#C5A55A]/30 text-[#C5A55A] disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ledger-totals">
        {["PENDING", "AVAILABLE", "SCHEDULED", "PAID", "REVERSED", "FROZEN"].map((s) => {
          const t = totals[s] || { count: 0, total_cents: 0 };
          return (
            <div key={s} className={`rounded-lg border ring-1 p-3 bg-[#111113] ${STATUS_COLORS[s] || "ring-white/10"}`} data-testid={`total-${s}`}>
              <div className="text-[10px] uppercase tracking-wider opacity-80">{s}</div>
              <div className="text-lg font-mono mt-1">{cents(t.total_cents)}</div>
              <div className="text-[10px] opacity-60">{t.count} rows</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2" data-testid="filters">
        <Filter className="w-4 h-4 text-white/40" />
        <select
          value={filters.status}
          onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
          className="bg-[#111113] border border-white/10 rounded px-2 py-1 text-xs text-white/80"
          data-testid="filter-status"
        >
          <option value="all">All statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={filters.commission_type}
          onChange={(e) => setFilters(f => ({ ...f, commission_type: e.target.value }))}
          className="bg-[#111113] border border-white/10 rounded px-2 py-1 text-xs text-white/80"
          data-testid="filter-type"
        >
          <option value="all">All types</option>
          <option value="CUSTOMER_COMMISSION">Customer</option>
          <option value="SPONSOR_COMMISSION">Sponsor</option>
          <option value="LEADERSHIP_COMMISSION">Leadership</option>
          <option value="REVERSAL">Reversal</option>
          <option value="AFFILIATE_BALANCE_SPEND">Balance spend</option>
        </select>
        <input
          type="text"
          value={filters.user_id}
          onChange={(e) => setFilters(f => ({ ...f, user_id: e.target.value }))}
          placeholder="user_id (recipient)"
          className="bg-[#111113] border border-white/10 rounded px-2 py-1 text-xs text-white/80 font-mono flex-1 max-w-xs"
          data-testid="filter-user-id"
        />
      </div>

      {/* Rows */}
      <div className="rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="ledger-table">            <thead className="bg-white/[0.03] text-white/40 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left py-2 px-3 font-medium">Earned</th>
                <th className="text-left py-2 px-3 font-medium">Type</th>
                <th className="text-left py-2 px-3 font-medium">Recipient</th>
                <th className="text-left py-2 px-3 font-medium">Source Customer</th>
                <th className="text-right py-2 px-3 font-medium">Rate</th>
                <th className="text-right py-2 px-3 font-medium">Gross</th>
                <th className="text-right py-2 px-3 font-medium">Commission</th>
                <th className="text-left py-2 px-3 font-medium">Status</th>
                <th className="text-left py-2 px-3 font-medium">Clears / Paid</th>
                <th className="text-left py-2 px-3 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="10" className="text-center py-8 text-white/30">No ledger rows match filters</td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-white/[0.04] hover:bg-white/[0.03] cursor-pointer"
                  data-testid={`ledger-row-${r.id}`}
                  onClick={() => setSelectedRow(r)}
                  title="Click to inspect"
                >
                  <td className="py-1.5 px-3 text-white/60 font-mono text-[10px]">
                    {r.earned_at ? new Date(r.earned_at).toLocaleString() : "—"}
                  </td>
                  <td className="py-1.5 px-3 text-white/80">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      r.compensation_profile === "PRO" ? "bg-[#C5A55A]/20 text-[#C5A55A]" : "bg-white/5 text-white/60"
                    }`}>
                      {r.commission_type?.replace("_COMMISSION", "")}
                      {r.compensation_profile === "PRO" ? " (PRO)" : ""}
                    </span>
                  </td>
                  <td className="py-1.5 px-3">
                    <div className="text-white/80">{r.recipient_name || "—"}</div>
                    <div className="text-white/30 font-mono text-[10px]">{r.recipient_user_id}</div>
                  </td>
                  <td className="py-1.5 px-3">
                    <div className="text-white/70">{r.source_customer_name || "—"}</div>
                    <div className="text-white/30 font-mono text-[10px]">{r.source_customer_id}</div>
                  </td>
                  <td className="py-1.5 px-3 text-right text-white/70 font-mono">
                    {(r.commission_rate * 100).toFixed(0)}%
                  </td>
                  <td className="py-1.5 px-3 text-right text-white/60 font-mono">{cents(r.gross_transaction_amount)}</td>
                  <td className={`py-1.5 px-3 text-right font-mono font-medium ${
                    r.commission_amount < 0 ? "text-red-300" : "text-emerald-300"
                  }`}>{cents(r.commission_amount)}</td>
                  <td className="py-1.5 px-3">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 ${STATUS_COLORS[r.status] || ""}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-white/50 font-mono text-[10px]">
                    {r.paid_at ? `PAID ${new Date(r.paid_at).toLocaleDateString()}` :
                     r.available_at ? `AVAIL ${new Date(r.available_at).toLocaleDateString()}` :
                     r.clears_at ? `→ ${new Date(r.clears_at).toLocaleDateString()}` : "—"}
                  </td>
                  <td className="py-1.5 px-3 text-white/50 text-[10px]">
                    {r.payment_source === "affiliate_balance" ? "AffBal" : (r.payment_source || "card")}
                    {r.subscription_id && <div className="text-white/30 font-mono">sub</div>}
                    {r.coin_pack_id && <div className="text-white/30 font-mono">{r.coin_pack_id}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {total > 0 && (
          <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.04] text-[11px] text-white/40 font-mono">
            <span data-testid="ledger-range">
              {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}
              –
              {Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-1">
              <span data-testid="ledger-page-indicator">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="p-1 rounded hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="ledger-prev-page"
                aria-label="Previous page"
              >
                &lsaquo;
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="p-1 rounded hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="ledger-next-page"
                aria-label="Next page"
              >
                &rsaquo;
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedRow && <InspectModal row={selectedRow} onClose={() => setSelectedRow(null)} />}
    </section>
  );
}
